import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users, HandCoins, Award, Lock, CheckCircle2 } from "lucide-react";
import { api, idr, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import LoadingState from "@/components/ui/loading-state";
import { PageHeader, TidakPunyaAkses, DEAL_STATUS_META, PLATFORM_LABEL, fmtPersen } from "./_shared";
// Formatter ER diimpor dari `Kol.jsx`, TIDAK disalin: dua halaman harus
// menampilkan "belum bisa dihitung" dengan cara yang persis sama, dan
// `_shared.jsx` di luar cakupan batch ini (CLAUDE.md: satu sumber).
import { fmtEr, fmtAngkaOpsional } from "./Kol";
import DealsPanel from "./DealsPanel";

export default function KolDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const bolehLihat = can("marketing_kol", "view");
  const [tab, setTab] = useState("deals");

  const { data: kol, loading, error, refetch } = useDataQuery(
    ["marketing", "kol-satu", id],
    () => api.get(`/marketing/kol/${id}`).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const { data: deals, loading: loadingDeals } = useDataQuery(
    ["marketing", "kol-deals", id, ""],
    () => api.get("/marketing/kol/deals", { params: { kol_id: id } }).then((r) => r.data),
    { enabled: bolehLihat },
  );

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Users} title="Detail KOL" />
        <TidakPunyaAkses label="KOL & Influencer" testid="mkt-koldet-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-kol-detail">
      <Link to="/marketing/kol" data-testid="mkt-koldet-kembali"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Kembali ke direktori KOL
      </Link>

      <LoadingState loading={loading} error={error} onRetry={refetch} varian="kartu" data-testid="mkt-koldet-loading">
        {kol && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-mono text-xs text-slate-400">{kol.code}</p>
                <h1 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-0.5 inline-flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" /> {kol.nama}
                  {!kol.is_active && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 align-middle">Nonaktif</span>}
                </h1>
                {/* `niche` nama baru, `kategori` nama lama — dokumen lama
                    tidak dimigrasi, jadi keduanya dibaca. */}
                <p className="text-sm text-slate-600 mt-1">{PLATFORM_LABEL[kol.platform] || kol.platform}{(kol.niche || kol.kategori) ? ` · ${kol.niche || kol.kategori}` : ""}</p>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-500">
                <span>Followers: <span className="font-mono font-semibold text-slate-800" data-testid="mkt-koldet-followers">{fmtNum(kol.followers)}</span></span>
                <span>Average View: <span className="font-mono font-semibold text-slate-800" data-testid="mkt-koldet-avg-view">{fmtAngkaOpsional(kol.average_view)}</span></span>
                <span>Average Like: <span className="font-mono font-semibold text-slate-800" data-testid="mkt-koldet-avg-like">{fmtAngkaOpsional(kol.average_like)}</span></span>
                <span>Average Comment: <span className="font-mono font-semibold text-slate-800" data-testid="mkt-koldet-avg-comment">{fmtAngkaOpsional(kol.average_comment)}</span></span>
                <span>ER by Followers: <span className="font-mono font-semibold text-slate-800" data-testid="mkt-koldet-er-followers">{fmtEr(kol.er_followers)}</span></span>
                <span>ER by Views: <span className="font-mono font-semibold text-slate-800" data-testid="mkt-koldet-er-views">{fmtEr(kol.er_views)}</span></span>
                {kol.no_hp && <span>HP: <span className="font-medium text-slate-700">{kol.no_hp}</span></span>}
                {/* Angka ER lama hasil input manual — field-nya sudah tidak
                    bisa diisi lagi, tapi yang terlanjur tersimpan tetap
                    diperlihatkan supaya datanya tidak hilang diam-diam. */}
                {kol.engagement_rate !== null && kol.engagement_rate !== undefined && (
                  <span>ER manual (data lama): <span className="font-mono font-semibold text-slate-500" data-testid="mkt-koldet-er-manual">{fmtPersen(kol.engagement_rate)}</span></span>
                )}
              </div>
            </div>
            {kol.rate_card && <p className="text-xs text-slate-500 mt-3 border-t border-slate-100 pt-3">Rate card: {kol.rate_card}</p>}
            {kol.catatan && <p className="text-xs text-slate-500 mt-1">Catatan: {kol.catatan}</p>}
          </div>
        )}
      </LoadingState>

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1 w-fit">
        <button onClick={() => setTab("deals")} data-testid="mkt-koldet-tab-deals"
                className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "deals" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <HandCoins className="w-4 h-4" /> Deals
        </button>
        <button onClick={() => setTab("scorecard")} data-testid="mkt-koldet-tab-scorecard"
                className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "scorecard" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <Award className="w-4 h-4" /> Scorecard
        </button>
      </div>

      {tab === "deals" ? (
        <DealsPanel kolId={id} />
      ) : (
        <Scorecard deals={deals || []} loading={loadingDeals} />
      )}
    </div>
  );
}

/** Scorecard sederhana — murni agregasi (jumlah/total/rata-rata) dari data
 *  deal yang SUDAH datang dari backend (nilai_deal, status, pelunasan_locked).
 *  TIDAK menyentuh field margin/ROI sama sekali (deal tidak membawa field
 *  itu) sehingga tidak perlu digerbangi `marketing_margin`. */
function Scorecard({ deals, loading }) {
  const stat = useMemo(() => {
    const total = deals.length;
    const totalNilai = deals.reduce((s, d) => s + Number(d.nilai_deal || 0), 0);
    const selesai = deals.filter((d) => d.status === "selesai").length;
    const terkunci = deals.filter((d) => d.pelunasan_locked).length;
    const byStatus = {};
    deals.forEach((d) => { byStatus[d.status] = (byStatus[d.status] || 0) + 1; });
    return { total, totalNilai, selesai, terkunci, byStatus, rata: total ? totalNilai / total : 0 };
  }, [deals]);

  return (
    <LoadingState loading={loading} varian="kartu" jumlahKartu={4} data-testid="mkt-koldet-scorecard-loading">
      <div className="space-y-4" data-testid="mkt-koldet-scorecard">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <CardStat icon={HandCoins} label="Total Deal" value={fmtNum(stat.total)} />
          <CardStat icon={CheckCircle2} label="Total Nilai Deal" value={idr(stat.totalNilai)} />
          <CardStat icon={Award} label="Rata-rata Nilai Deal" value={idr(stat.rata)} />
          <CardStat icon={Lock} label="Pelunasan Terkunci" value={fmtNum(stat.terkunci)} aksen={stat.terkunci > 0 ? "rose" : "slate"} />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <p className="text-sm font-semibold text-slate-900 mb-3">Sebaran Status Deal</p>
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(stat.byStatus).length === 0 && <p className="text-sm text-slate-400">Belum ada deal.</p>}
            {Object.entries(stat.byStatus).map(([k, v]) => (
              <span key={k} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${DEAL_STATUS_META[k]?.chip || "bg-slate-100 text-slate-600"}`}>
                {DEAL_STATUS_META[k]?.label || k} <span className="font-mono font-semibold">{v}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </LoadingState>
  );
}

function CardStat({ icon: Icon, label, value, aksen = "slate" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${aksen === "rose" ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-600"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-xl font-bold text-slate-900 mt-0.5 font-mono">{value}</p>
    </div>
  );
}
